/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import PropTypes from 'prop-types'
import React, {Fragment, forwardRef, useEffect, useRef, useState} from 'react'
import {Link as RouteLink} from 'react-router-dom'
import TextImageCard from '../text-image-card'

// Components
import {
    Box,
    Container,
    Fade,
    Flex,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Stack,
    useDisclosure,
    useTheme
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '../link'

import {ChevronDownIcon} from '@salesforce/retail-react-app/app/components/icons'
import {colors} from '../../constants'
import MegaNavLinks from '../mega-nav-links'
import Overlay from '../overlay'
import MenuItemSkeleton from './partials/menu-item-skeleton'
const ChevronIconTrigger = forwardRef(function ChevronIconTrigger(props, ref) {
    return (
        <Box {...props} ref={ref}>
            <ChevronDownIcon />
        </Box>
    )
})

const ListMenuTrigger = ({item, isOpen, onOpen, onClose}) => {
    const theme = useTheme()
    const {baseStyle} = theme.components.ListMenu

    const keyMap = {
        Escape: () => onClose(),
        Enter: () => onOpen()
    }

    // TODO: Add proper condition to check if the item is heading or Link
    //  TODO: Add title case for text
    return (
        <Box {...baseStyle.listMenuTriggerContainer}>
            <Link
                type={item.type || 'tertiary'}
                {...(item.url || item.externalUrl
                    ? {
                          url: item.url,
                          isExternal: item.isExternal,
                          buttonStyles: {
                              lineHeight: '18.75px',
                              height: '44px',
                              display: 'flex',
                              alignItems: 'center'
                          }
                      }
                    : {
                          buttonStyles: {
                              height: '44px',
                              display: 'flex',
                              alignItems: 'center'
                          }
                      })}
                onMouseOver={onOpen}
                {...baseStyle.listMenuTriggerLink}
                {...(isOpen ? baseStyle.listMenuTriggerLinkActive : {})}
                _active={{background: 'unset'}}
                text={item.url || item.externalUrl ? item.text : item.heading}
            />
            <PopoverTrigger>
                <Link
                    {...item}
                    as={RouteLink}
                    onMouseOver={onOpen}
                    onKeyDown={(e) => {
                        keyMap[e.key]?.(e)
                    }}
                    {...baseStyle.listMenuTriggerLinkIcon}
                >
                    <Fade in={true}>
                        <ChevronIconTrigger {...baseStyle.selectedButtonIcon} />
                    </Fade>
                </Link>
            </PopoverTrigger>
        </Box>
    )
}
ListMenuTrigger.propTypes = {
    name: PropTypes.string,
    isOpen: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    item: PropTypes.shape({
        url: PropTypes.string,
        externalUrl: PropTypes.string,
        text: PropTypes.string,
        type: PropTypes.string,
        isExternal: PropTypes.bool,
        heading: PropTypes.string
    })
}

const ListMenuContent = ({items, header, onClose}) => {
    const theme = useTheme()
    const {baseStyle} = theme.components.ListMenu
    const bannerData =
        header?.headerCollection?.items[0]?.bannerCollection?.items

    return (
        <PopoverContent
            data-testid="popover-menu"
            {...baseStyle.popoverContent}
            top="67px"
        >
            <PopoverBody>
                <Container
                    as={Stack}
                    {...baseStyle.popoverContainer}
                    display="flex"
                    flexDirection="row"
                >
                    <MegaNavLinks items={items} onClose={onClose} />
                    <Flex
                        width="50%"
                        gap="17px"
                        alignItems="start"
                        justifyContent="flex-end"
                    >
                        {bannerData?.map((item, index) => {
                            return (
                                <TextImageCard
                                    key={index}
                                    img={{
                                        src: item?.media?.desktop.url,
                                        alt: item?.text
                                    }}
                                    link={item.cta}
                                    text={item?.text}
                                    onLinkClick={onClose}
                                    cardAlignment={baseStyle.cardAlignment}
                                    textAlignment={baseStyle.textAlignment}
                                    hoverStyles={{
                                        transition: 'all 0.3s',
                                        _hover: {
                                            boxShadow: `1px 1px 18px ${item?.hoverColor}`
                                        }
                                    }}
                                    boxShadow={
                                        index === 0
                                            ? `1px 1px 18px 0 ${colors.softOrange}`
                                            : 'none'
                                    }
                                />
                            )
                        })}
                    </Flex>
                </Container>
            </PopoverBody>
        </PopoverContent>
    )
}
ListMenuContent.propTypes = {
    items: PropTypes.array,
    onClose: PropTypes.func,
    header: PropTypes.object
}

const ListMenuPopover = ({items, item, header}) => {
    const initialFocusRef = useRef()
    const {isOpen, onClose, onOpen} = useDisclosure()
    return (
        <Box onMouseLeave={onClose}>
            {isOpen && items && <Overlay />}
            <Popover
                isLazy
                placement={'bottom-start'}
                initialFocusRef={initialFocusRef}
                onOpen={onOpen}
                onClose={onClose}
                isOpen={isOpen}
                variant="fullWidth"
            >
                <Fragment>
                    <ListMenuTrigger
                        item={item}
                        isOpen={isOpen}
                        onOpen={onOpen}
                        onClose={onClose}
                    />
                    {items && (
                        <ListMenuContent
                            items={items}
                            header={header}
                            initialFocusRef={initialFocusRef}
                            onClose={onClose}
                        />
                    )}
                </Fragment>
            </Popover>
        </Box>
    )
}

ListMenuPopover.propTypes = {
    items: PropTypes.array,
    item: PropTypes.shape({
        url: PropTypes.string,
        text: PropTypes.string,
        name: PropTypes.string,
        type: PropTypes.string,
        isExternal: PropTypes.bool
    }),
    name: PropTypes.string,
    header: PropTypes.object
}

/**
 * This is the navigation component used for desktop devices. Holds the site navigation,
 * providing users with a way to access all product categories and other important pages.
 * The submenus are open when the user moves the mouse over the trigger and for A11y when
 * users use the keyboard Tab key to focus over the chevron icon and press Enter.
 *
 * @param maxColumns The maximum number of columns that we want to use per row inside the ListMenu.
 * @param root
 */
const ListMenu = ({header, headerMegaNav, setSearchShow}) => {
    const theme = useTheme()
    const {baseStyle} = theme.components.ListMenu
    const [ariaBusy, setAriaBusy] = useState(true)
    const menuItems =
        headerMegaNav?.headerCollection?.items[0]?.leftCollection?.items

    useEffect(() => {
        setAriaBusy(false)
    }, [])

    return (
        <nav
            id="list-menu"
            aria-label="main"
            aria-live="polite"
            aria-busy={ariaBusy}
            aria-atomic="true"
        >
            <Flex {...baseStyle.container}>
                {menuItems?.length ? (
                    <Stack
                        direction={'row'}
                        spacing={0}
                        {...baseStyle.stackContainer}
                    >
                        {menuItems?.map?.((item) => {
                            return (
                                <Box
                                    key={item?.sys?.id}
                                    onMouseOver={() => setSearchShow(false)}
                                >
                                    <ListMenuPopover
                                        setSearchShow={setSearchShow}
                                        item={item}
                                        items={item?.itemsCollection?.items}
                                        header={header}
                                    />
                                </Box>
                            )
                        })}
                    </Stack>
                ) : (
                    <Box display="flex">
                        <MenuItemSkeleton />
                        <MenuItemSkeleton />
                        <MenuItemSkeleton />
                        <MenuItemSkeleton />
                    </Box>
                )}
            </Flex>
        </nav>
    )
}

ListMenu.displayName = 'ListMenu'

ListMenu.propTypes = {
    header: PropTypes.object,
    setSearchShow: PropTypes.func
}

export default ListMenu
